import {
  BillingInterval as Interval,
  CancelFeedback,
  PaymentStatus,
  SubscriptionTier,
} from '@/store/subscriptions/subscriptions.types';
import type { BillingInterval, Subscription } from '@/store/subscriptions/subscriptions.types';

import type { Renewal } from './subscription.types';

/**
 * Presentation of a real subscription. Kept apart from `subscription.constants`, which
 * still describes the mocked full/limited seat model used by the detail view.
 */

/**
 * A trial is the one payment state the list calls out: it runs out on its own, so the
 * owner has something to do about it. Every other state is the billing portal's to show.
 */
export const isTrialing = (subscription: Subscription | null) =>
  subscription?.paymentStatus === PaymentStatus.TRIALING;

/**
 * Rows the server treats as dead — `DeadPaymentStatuses` in the People service's
 * `internal/pkg/database/subscription.go`. A row in one of these states carries a tier,
 * an interval and seat counts, but nothing was provisioned or is still provisioned behind
 * it, and the server excludes it when it looks for a service's live subscription.
 *
 * Typed to accept `null` so an unmapped `payment_status` falls outside the set and keeps
 * whatever tier it claims.
 */
const DEAD_STATUSES = new Set<PaymentStatus | null>([
  PaymentStatus.INCOMPLETE,
  PaymentStatus.INCOMPLETE_EXPIRED,
  PaymentStatus.CANCELED,
]);

/**
 * A row the server counts as the service's current subscription — `GetLiveSubscriptionByServiceID`
 * in the People service. Something was provisioned behind it and still is. A dead row is not
 * one: it carries a tier, an interval and seat counts, but the server looks straight past it,
 * which is why it allows a fresh checkout for that service.
 */
export const isLiveSubscription = (
  subscription: Subscription | null,
): subscription is Subscription => !!subscription && !DEAD_STATUSES.has(subscription.paymentStatus);

/**
 * Whether the vessel actually holds a paid plan. No subscription row, a `harbor` row and
 * an unpaid checkout all read the same to the owner: the free plan.
 *
 * This is also what picks the endpoint a plan change goes to, because the server's two
 * halves divide on exactly this line: `PATCH /subscriptions/{id}` needs a live Stripe
 * subscription to edit (`ErrSubscriptionNotActive` without one), and
 * `POST /subscriptions/checkout` refuses a service that already has a live *paid* row
 * (`ErrSubscriptionExists`) while explicitly admitting a harbor one.
 */
export const isPaidPlan = (subscription: Subscription | null): subscription is Subscription =>
  isLiveSubscription(subscription) && subscription.tier !== SubscriptionTier.HARBOR;

/**
 * A lifetime plan an admin granted: never billed, never expiring, and not something the vessel
 * can change, take over or cancel. It still counts as a paid plan for everything it switches on.
 */
export const isLifetimePlan = (subscription: Subscription | null): boolean =>
  isPaidPlan(subscription) && subscription.manuallyGranted;

/**
 * Timestamps arrive as Go `time.Time`. An unset one marshals either to `''` — already
 * mapped to `null` — or to the year-1 zero value, which parses cleanly and would print
 * as "Jan 1, 1"; Stripe's own unset dates come back at the Unix epoch. Anything at or
 * before epoch is therefore not a date the user should be shown.
 */
export const timestamp = (raw: string | null): number | null => {
  if (!raw) return null;
  const at = Date.parse(raw);
  return Number.isNaN(at) || at <= 0 ? null : at;
};

/**
 * What happens when the current period runs out. `cancel_at_period_end` outranks the
 * next payment: the subscription is still live but nothing more will be charged, so the
 * date the owner needs is when access stops, not a renewal that will not happen.
 */
export const renewalOf = (subscription: Subscription | null): Renewal => {
  if (!isPaidPlan(subscription)) return { kind: 'none', at: null };

  if (subscription.cancelAtPeriodEnd) {
    const at = timestamp(subscription.cancelAt);
    return at ? { kind: 'ends', at } : { kind: 'none', at: null };
  }

  const at = timestamp(subscription.nextPaymentAt);
  return at ? { kind: 'renews', at } : { kind: 'none', at: null };
};

export const TIER_NAME: Record<SubscriptionTier, string> = {
  [SubscriptionTier.HARBOR]: 'Harbor',
  [SubscriptionTier.NAVIGATE]: 'Navigate',
  [SubscriptionTier.COMMAND]: 'Command',
};

const INTERVAL_NAME: Record<BillingInterval, string> = {
  monthly: 'Monthly',
  annually: 'Annually',
};

/** Suffix for an amount charged once per interval — `$19.99/mo`. */
export const INTERVAL_UNIT: Record<BillingInterval, string> = {
  monthly: '/mo',
  annually: '/yr',
};

/** How often the amount recurs, for the line under a total — "Billed monthly". */
export const billedEvery = (interval: BillingInterval) =>
  `Billed ${INTERVAL_NAME[interval].toLowerCase()}`;

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

/**
 * `seats` is the billable seat count and `crew_seats` a separate add-on — named
 * Management and Crew here, the same two labels the checkout form steps. Both are always
 * stated, including a zero, so the split is readable without opening the vessel. The API
 * does not report how many are occupied, so this is the allocation, not usage.
 */
const seatsSummary = (subscription: Subscription): string =>
  [plural(subscription.seats, 'management seat'), plural(subscription.crewSeats, 'crew seat')].join(
    ' · ',
  );

/**
 * The plan's name alone — `Harbor` for every shape of the free plan, otherwise the tier.
 * This is the part that fits in a heading; `planSummary` states the same name with the
 * interval and seat counts after it.
 */
export const planName = (subscription: Subscription | null): string => {
  if (!isPaidPlan(subscription)) return TIER_NAME[SubscriptionTier.HARBOR];
  return subscription.tier ? TIER_NAME[subscription.tier] : 'Unknown plan';
};

export const planSummary = (subscription: Subscription | null): string => {
  // The free plan has no seat counts to state — the tier name is the whole line.
  if (!isPaidPlan(subscription)) return planName(subscription);

  // A lifetime plan has no billing interval; where one would go, it is named for what it is.
  const interval = subscription.manuallyGranted
    ? 'Lifetime'
    : subscription.interval
      ? INTERVAL_NAME[subscription.interval]
      : null;
  return [planName(subscription), interval, seatsSummary(subscription)].filter(Boolean).join(' · ');
};

/**
 * Cancellation reasons, optional on `POST /subscriptions/{id}/cancel`. Stripe's own
 * feedback set, so the wording follows what it means to the owner rather than the enum.
 */
export const CANCEL_FEEDBACK_CHOICES = [
  { value: CancelFeedback.TOO_EXPENSIVE, label: 'Too expensive' },
  { value: CancelFeedback.UNUSED, label: 'Not using it enough' },
  { value: CancelFeedback.MISSING_FEATURES, label: 'Missing features' },
  { value: CancelFeedback.SWITCHED_SERVICE, label: 'Switched to another service' },
  { value: CancelFeedback.LOW_QUALITY, label: 'Not good enough' },
  { value: CancelFeedback.CUSTOMER_SERVICE, label: 'Customer service' },
  { value: CancelFeedback.OTHER, label: 'Something else' },
];

// ---------------------------------------------------------------------------
// Checkout form options — the shape POST /subscriptions/checkout accepts
// ---------------------------------------------------------------------------

/** `harbor` is not purchasable, so only these two can be checked out. */
export const TIER_CHOICES = [
  { tier: SubscriptionTier.NAVIGATE, label: TIER_NAME[SubscriptionTier.NAVIGATE] },
  { tier: SubscriptionTier.COMMAND, label: TIER_NAME[SubscriptionTier.COMMAND] },
] as const;

/**
 * Annually is supported by the API but not offered at checkout yet, so monthly is the
 * only choice. Drop it from this set to bring it back — the form renders a radio group
 * or a fixed row depending on how many are left.
 */
const HIDDEN_INTERVALS = new Set<BillingInterval>([Interval.ANNUALLY]);

export const INTERVAL_CHOICES = [
  { interval: Interval.MONTHLY, label: INTERVAL_NAME[Interval.MONTHLY] },
  { interval: Interval.ANNUALLY, label: INTERVAL_NAME[Interval.ANNUALLY] },
].filter(({ interval }) => !HIDDEN_INTERVALS.has(interval));

/** The interval a new checkout starts on — the first one still offered. */
export const DEFAULT_INTERVAL = INTERVAL_CHOICES[0].interval;

/**
 * Seat counts are stepped rather than picked from a list: `seats` starts at 1 and
 * `crew_seats` — an optional add-on — at 0. The spec documents no upper bound, so
 * neither is capped.
 */
export const MIN_SEATS = 1;
export const MIN_CREW_SEATS = 0;
